import React, { useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import { baseURL } from '../../axios'
import { ChatCard } from '../site'

/** Where the hosted snippet is served: the API's origin. */
const embedUrl = `${new URL(baseURL, window.location.href).origin}/embed/v1.js`

export type PreviewConfig = {
    locale: string
    name: string
    welcome: string
    color: string
    textColor: string
    position: 'right' | 'left'
    radius: number
    label: string
    credit: boolean
    privacyUrl: string
    suggestions: string[]
}

export type TestHistory = { role: 'visitor' | 'assistant'; content: string; vacancyIds?: string[] }[]

export type ChatPreviewHandle = {
    /** Clears the test chat. */
    reset: () => void
}

type ChatPreviewProps = {
    config: PreviewConfig
    /** Makes the preview a test chat: answers the visitor's questions. Without it, the preview only shows the look. */
    onQuestion?: (content: string, messages: TestHistory) => Promise<{ message: string; cards: ChatCard[] }>
    ref?: React.Ref<ChatPreviewHandle>
    className?: string
}

/**
 * The hosted widget in an iframe, so the preview has the same size, fonts and wrapping as on the website. New settings
 * are sent to the iframe instead of reloading it, so a test chat continues while the settings change.
 */
export function ChatPreview({ config, onQuestion, ref, className }: ChatPreviewProps) {
    const frame = useRef<HTMLIFrameElement>(null)
    const widgetConfig = {
        apiUrl: '',
        siteKey: '',
        locale: config.locale,
        color: config.color,
        textColor: config.textColor,
        position: config.position,
        radius: config.radius,
        label: config.label,
        credit: config.credit,
        privacyUrl: config.privacyUrl,
        suggestions: config.suggestions,
        preview: { name: config.name, welcome: config.welcome, test: Boolean(onQuestion) },
    }
    const latest = useRef(widgetConfig)
    latest.current = widgetConfig
    const answer = useRef(onQuestion)
    answer.current = onQuestion

    // The iframe has the page's own origin (srcdoc), so messages are only exchanged with that origin.
    const post = (message: object) => frame.current?.contentWindow?.postMessage(message, window.location.origin)

    useImperativeHandle(ref, () => ({
        reset: () => post({ type: 'evaa-test-reset' }),
    }))

    // Built once: a new srcdoc would reload the iframe on every change.
    const srcDoc = useMemo(() => {
        // "<" is escaped so text such as "</script>" in the welcome message cannot end the script.
        const json = JSON.stringify(latest.current).replace(/</g, '\\u003c')

        return `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;height:100%;background:transparent;overflow:hidden}</style></head>
<body><script>window.EvaaChat=${json};</script><script src="${embedUrl}"></script></body></html>`
    }, [])

    const serialized = JSON.stringify(widgetConfig)
    const send = () => post({ type: 'evaa-preview', config: latest.current })

    useEffect(() => {
        send()
        // Only when the settings change; send reads the latest settings from the ref.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serialized])

    // Questions from the test chat in the widget are answered here, with the user's login.
    useEffect(() => {
        const listener = (
            event: MessageEvent<{ type?: string; id?: string; content?: string; messages?: TestHistory }>
        ) => {
            const data = event.data

            if (
                event.source !== frame.current?.contentWindow ||
                event.origin !== window.location.origin ||
                data?.type !== 'evaa-test-send' ||
                !data.id ||
                !data.content ||
                !answer.current
            ) {
                return
            }

            answer
                .current(data.content, data.messages ?? [])
                .then((result) => post({ type: 'evaa-test-answer', id: data.id, ...result }))
                .catch(() => post({ type: 'evaa-test-error', id: data.id }))
        }

        window.addEventListener('message', listener)

        return () => window.removeEventListener('message', listener)
    }, [])

    return (
        <iframe
            ref={frame}
            title="Preview"
            srcDoc={srcDoc}
            onLoad={send}
            className={className ?? 'block h-[600px] w-full rounded-lg border-0 bg-neutral-100'}
        />
    )
}
